/**
 * A row of larger radio cards, for the choices that deserve more than a pill.
 *
 * Shape and Output were the only Next.js selectors not built from pills, and for a reason
 * worth keeping: the aspect-ratio control *shows* the three proportions, and seeing them side
 * by side is the control — a row of words would not be the same thing. Output pairs each
 * option with a sentence about what it produces, which a pill has no room for. The mode
 * choice now uses this too: it decides which form, which endpoint and which half of the
 * page's remaining controls appear, which is not a pill-sized decision.
 *
 * **Stays N-across at every width rather than stacking**, except where a caller asks for
 * `stackOnMobile` — Shape must stay three-across because the comparison is the point, while
 * two cards carrying a sentence each are unreadable side by side at 375px. Elsewhere the
 * padding and type scale down instead: the desktop sizing left "Landscape" wrapping mid-word
 * inside its own card.
 *
 * ## Naming
 *
 * The accessible name is the label alone, via `aria-label`, with `detail` attached through
 * `aria-describedby`. Letting the DOM text form the name would fold the whole sentence into
 * it — "Supply an image Upload a photo you already have and say how it should change" — which
 * is worse to listen to and makes the option impossible to address precisely. Name is what
 * the thing *is*; description is what it does.
 */

import { ButtonBase, Stack, Typography } from "@mui/material";
import { useId, type ReactNode } from "react";

import { useRovingRadioGroup } from "@/components/generate/useRovingRadioGroup";
import { RADIUS_INNER_PX, jp } from "@/theme/tokens";

export interface CardOption<T> {
  value: T;
  label: string;
  /** A sentence under the label, or the ratio preview frame. */
  detail?: ReactNode;
  /** Rendered above the label — the aspect-ratio preview. */
  preview?: ReactNode;
  /** Rendered left of the label, for the mode cards. */
  icon?: ReactNode;
}

export interface CardGroupProps<T> {
  label: string;
  options: ReadonlyArray<CardOption<T>>;
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  columns: number;
  /** One column below `sm`. For cards carrying a sentence, which cannot share a phone row. */
  stackOnMobile?: boolean;
}

export function CardGroup<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled = false,
  columns,
  stackOnMobile = false,
}: CardGroupProps<T>) {
  const idBase = useId();
  const { groupProps, getRadioProps } = useRovingRadioGroup(
    options.map((option) => option.value),
    value,
    onChange,
    disabled,
  );

  return (
    <Stack
      role="radiogroup"
      aria-label={label}
      sx={{
        display: "grid",
        gridTemplateColumns: stackOnMobile
          ? { xs: "minmax(0, 1fr)", sm: `repeat(${columns}, minmax(0, 1fr))` }
          : `repeat(${columns}, minmax(0, 1fr))`,
        gap: { xs: 1, sm: 1.5 },
      }}
      {...groupProps}
    >
      {options.map((option, index) => {
        const active = option.value === value;
        const detailId = option.detail ? `${idBase}-${option.value}-detail` : undefined;
        return (
          <ButtonBase
            key={option.value}
            role="radio"
            aria-checked={active}
            aria-label={option.label}
            aria-describedby={detailId}
            disabled={disabled}
            onClick={() => onChange(option.value)}
            {...getRadioProps(index)}
            sx={{
              display: "flex",
              flexDirection: option.icon ? "row" : "column",
              alignItems: option.preview ? "center" : "flex-start",
              gap: option.icon ? 1.5 : 0.75,
              minWidth: 0,
              px: { xs: 1, sm: 2 },
              py: 1.5,
              textAlign: option.preview ? "center" : "left",
              borderRadius: `${RADIUS_INNER_PX}px`,
              // A 2px border on the active card and 1px otherwise would shift the layout by a
              // pixel on selection, so both are 2px and only the colour changes.
              border: "2px solid",
              borderColor: active ? jp.navy : jp.line,
              backgroundColor: active ? "rgba(51,105,255,0.05)" : "transparent",
              "&:hover": { backgroundColor: active ? "rgba(51,105,255,0.08)" : "rgba(250,240,225,0.6)" },
              "&.Mui-disabled": { opacity: 0.5 },
            }}
          >
            {option.icon ? (
              <Stack
                aria-hidden
                sx={{ color: active ? jp.navyDark : jp.slate, flexShrink: 0, pt: 0.25 }}
              >
                {option.icon}
              </Stack>
            ) : null}
            {option.preview}
            <Stack sx={{ minWidth: 0, alignItems: option.preview ? "center" : "flex-start" }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
              >
                {option.label}
              </Typography>
              {option.detail ? (
                <Typography
                  id={detailId}
                  variant="caption"
                  color="text.secondary"
                  // Floor of 11px. The ported 10px (`0.625rem`) was below the size at which
                  // this palette's muted grey stays comfortable to read, and these lines are
                  // the only explanation some of these options get.
                  sx={{ fontSize: { xs: "0.6875rem", sm: "0.75rem" }, lineHeight: 1.4 }}
                >
                  {option.detail}
                </Typography>
              ) : null}
            </Stack>
          </ButtonBase>
        );
      })}
    </Stack>
  );
}
